import styled from 'styled-components'

export type ProgressAiSuggestion = {
  item_id: number
  tracker: string
  job_type: string
  change: string
  confidence: string
  evidence: string
}

export const changeLabel = (change: string) => {
  const normalized = change.replace(/\s/g, '').toLowerCase()
  if (normalized === '1to0' || normalized === '1→0' || normalized === '1>0') {
    return '1to0'
  }
  if (normalized === '0to1' || normalized === '0→1' || normalized === '0>1') {
    return '0to1'
  }
  return change
}

export const isToOne = (change: string) => changeLabel(change) === '0to1'

export const suggestionActionLabel = (change: string) => {
  if (isToOne(change)) return 'Mark Installed (0 > 1)'
  if (changeLabel(change) === '1to0') return 'Mark Uninstalled (1 > 0)'
  return change
}

export const shortChangeLabel = (change: string) => {
  if (isToOne(change)) return '0 → 1'
  if (changeLabel(change) === '1to0') return '1 → 0'
  return change
}

const ChangeRow = styled.div`
  border: 1px solid #e8ecf0;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: #fafbfc;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
`

const TrackerJob = styled.div`
  color: #333;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.35;
  margin-bottom: 6px;
`

const MetaLine = styled.div`
  font-size: 12px;
  color: #555;
  line-height: 1.4;
  margin-bottom: 2px;
`

const MetaKey = styled.span`
  color: #777;
`

const SuggestionValue = styled.span<{ $toOne: boolean }>`
  font-weight: 700;
  color: ${p => (p.$toOne ? '#2e7d32' : '#c62828')};
`

const Evidence = styled.div`
  font-size: 12px;
  color: #444;
  line-height: 1.4;
  margin-top: 6px;
`

export const ProgressAiSuggestionCard = ({
  suggestion,
}: {
  suggestion: ProgressAiSuggestion
}) => (
  <ChangeRow>
    <TrackerJob>
      {suggestion.tracker}
      {suggestion.job_type ? ` / ${suggestion.job_type}` : ''}
    </TrackerJob>
    <MetaLine>
      <MetaKey>Item ID: </MetaKey>
      {suggestion.item_id}
    </MetaLine>
    <MetaLine>
      <MetaKey>Suggestion: </MetaKey>
      <SuggestionValue $toOne={isToOne(suggestion.change)}>
        {suggestionActionLabel(suggestion.change)}
      </SuggestionValue>
    </MetaLine>
    {suggestion.evidence ? <Evidence>{suggestion.evidence}</Evidence> : null}
  </ChangeRow>
)

export const AiChangeBadge = styled.span<{ $toOne: boolean }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: ${p => (p.$toOne ? '#2e7d32' : '#c62828')};
  white-space: nowrap;
`

/** Stable key for matching an AI suggestion to a specific review job row. */
export const aiSuggestionJobKey = (
  itemId: number | string,
  jobTypeName: string,
) => `${itemId}::${(jobTypeName || '').trim().toLowerCase()}`

export const matchSuggestionsForJob = (
  suggestionsByJobKey: Record<string, ProgressAiSuggestion[]>,
  itemId: number,
  jobTypeName: string,
): ProgressAiSuggestion[] =>
  suggestionsByJobKey[aiSuggestionJobKey(itemId, jobTypeName)] || []

